import { Plus, Trash2 } from 'lucide-react';
import { SocialPlatform } from '@mg/shared';
import { Select } from '../../components/ui/Field';
import { platformName } from '../social/platforms';
import type { EditableSlot } from './scheduleDraft';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const minuteToTime = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const timeToMinute = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/**
 * One schedule's week: a card per day, Sunday to Saturday, each with its times (time, platform,
 * Enabled, remove) and "Add time". The day cards are the page's original ones, moved here unchanged
 * when Scheduler Settings gained a schedule per category; they now draw whichever schedule is selected.
 */
export function WeeklyDays({
  slots,
  readOnly,
  addSlot,
  removeSlot,
  patchSlot,
}: {
  /** The selected schedule's times, every day of the week. */
  slots: EditableSlot[];
  readOnly: boolean;
  addSlot: (dayOfWeek: number) => void;
  removeSlot: (key: string) => void;
  patchSlot: (key: string, patch: Partial<Omit<EditableSlot, 'key'>>) => void;
}) {
  return (
    <div className="space-y-5">
      {DAY_NAMES.map((name, day) => {
        const daySlots = slots
          .filter((s) => s.dayOfWeek === day)
          .sort((a, b) => a.minute - b.minute);
        return (
          <div key={day} className="rounded-2xl border border-line p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-base font-semibold text-ink">{name}</p>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => addSlot(day)}
                  className="focus-ring inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-action hover:bg-tint"
                >
                  <Plus className="h-4 w-4" aria-hidden /> Add time
                </button>
              )}
            </div>
            {daySlots.length === 0 ? (
              <p className="text-sm text-ink-faint">No posting times.</p>
            ) : (
              <div className="space-y-2.5">
                {daySlots.map((s) => (
                  <div key={s.key} className="flex items-center gap-3">
                    <input
                      type="time"
                      value={minuteToTime(s.minute)}
                      disabled={readOnly}
                      onChange={(e) => patchSlot(s.key, { minute: timeToMinute(e.target.value) })}
                      className="focus-ring shrink-0 rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink disabled:opacity-60"
                    />
                    <Select
                      value={s.platform ?? ''}
                      disabled={readOnly}
                      onChange={(e) =>
                        patchSlot(s.key, {
                          platform: e.target.value ? (e.target.value as SocialPlatform) : null,
                        })
                      }
                      className="min-w-0 flex-1"
                      aria-label="Platform"
                    >
                      <option value="">Any platform</option>
                      {Object.values(SocialPlatform).map((p) => (
                        <option key={p} value={p}>
                          {platformName(p)}
                        </option>
                      ))}
                    </Select>
                    <label className="flex shrink-0 items-center gap-1.5 text-sm text-ink-label">
                      <input
                        type="checkbox"
                        checked={s.enabled}
                        disabled={readOnly}
                        onChange={(e) => patchSlot(s.key, { enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-line-input accent-action"
                      />
                      Enabled
                    </label>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeSlot(s.key)}
                        aria-label="Remove slot"
                        className="focus-ring shrink-0 rounded-md p-1.5 text-ink-faint hover:bg-red-50 dark:hover:bg-error-dark-tint hover:text-red-600 dark:hover:text-error-dark"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
